import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Gavel, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { atlasApi, AtlasUser, Ocorrencia } from '../shared/atlasApi';
import { BadgeTipo, BadgeStatusOcorrencia } from './BadgeOcorrencia';
import ModalOcorrencia from './ModalOcorrencia';

interface Props {
  user: AtlasUser;
  funccodi: number;
  podeEditar: boolean;
  nome?: string;
}

function fdata(s?: string | null) {
  if (!s) return '—';
  try { return new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR'); } catch { return s; }
}

/** Histórico disciplinar de um colaborador — usado na aba da ficha. */
const HistoricoDisciplinar: React.FC<Props> = ({ user, funccodi, podeEditar, nome }) => {
  const [itens, setItens] = useState<Ocorrencia[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [modal, setModal] = useState<{ aberto: boolean; editar: Ocorrencia | null }>({ aberto: false, editar: null });

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getOcorrenciasColaborador(user, funccodi)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar ocorrências'))
      .finally(() => setLoading(false));
  }, [user, funccodi]);

  useEffect(() => carregar(), [carregar]);

  const excluir = async (o: Ocorrencia) => {
    if (!window.confirm('Remover esta ocorrência do histórico ativo?')) return;
    try {
      await atlasApi.excluirOcorrencia(user, o._id);
      carregar();
    } catch (e: any) {
      setErro(e.message || 'Erro ao excluir');
    }
  };

  const contagem = itens.reduce<Record<string, number>>((acc, o) => {
    acc[o.tipo] = (acc[o.tipo] || 0) + 1;
    return acc;
  }, {});
  const totalDisciplinar = (contagem.advertencia_verbal || 0) + (contagem.advertencia_escrita || 0) + (contagem.suspensao || 0);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando ocorrências...</div>;

  return (
    <div className="space-y-4">
      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="text-sm text-gray-500">
          {totalDisciplinar === 0
            ? 'Nenhuma medida disciplinar registrada.'
            : `${totalDisciplinar} medida${totalDisciplinar === 1 ? '' : 's'} disciplinar${totalDisciplinar === 1 ? '' : 'es'} no histórico.`}
        </div>
        {podeEditar && (
          <button
            onClick={() => setModal({ aberto: true, editar: null })}
            className="ml-auto px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nova ocorrência
          </button>
        )}
      </div>

      {itens.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">
          <Gavel className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          Nenhuma ocorrência registrada para este colaborador.
        </div>
      ) : (
        <ul className="space-y-3">
          {itens.map(o => (
            <li key={o._id} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <BadgeTipo tipo={o.tipo} />
                    <BadgeStatusOcorrencia status={o.status} />
                    <span className="text-xs text-gray-400">{fdata(o.data)}</span>
                    {o.gravidade === 'grave' && <span className="text-[11px] font-bold text-rose-600 uppercase">Grave</span>}
                  </div>
                  <div className="font-medium text-gray-900 mt-2">{o.motivo}</div>
                  {o.descricao && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{o.descricao}</p>}
                  <div className="text-xs text-gray-400 mt-2 flex flex-wrap gap-x-3">
                    {o.dias_suspensao ? <span>{o.dias_suspensao} dia(s) de suspensão{o.data_inicio_suspensao ? ` a partir de ${fdata(o.data_inicio_suspensao)}` : ''}</span> : null}
                    {o.aplicada_por && <span>Aplicada por {o.aplicada_por}</span>}
                    {o.testemunhas && o.testemunhas.length > 0 && <span>Testemunhas: {o.testemunhas.join(', ')}</span>}
                    {o.ciente_em && <span>Ciente em {fdata(o.ciente_em)}</span>}
                  </div>
                  {o.documento_url && (
                    <a href={o.documento_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-600 hover:underline mt-2">
                      <ExternalLink className="w-3 h-3" /> Documento assinado
                    </a>
                  )}
                </div>
                {podeEditar && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setModal({ aberto: true, editar: o })} title="Editar" className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => excluir(o)} title="Remover" className="p-2 rounded-lg text-gray-400 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal.aberto && (
        <ModalOcorrencia
          user={user}
          ocorrencia={modal.editar}
          funccodiFixo={funccodi}
          nomeFixo={nome}
          onClose={() => setModal({ aberto: false, editar: null })}
          onSalvo={() => { setModal({ aberto: false, editar: null }); carregar(); }}
        />
      )}
    </div>
  );
};

export default HistoricoDisciplinar;
